import React from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import UserOutlined from '@src/assets/images/user-outlined.svg';
import Crown from '@src/assets/images/crown.svg';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { PROVIDE_EMAIL_URL } from '@src/constants/email';
import { COLOR } from '@src/styles/color';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import CommonButton from '@src/new-components/Button';
import CommonInput from '@src/new-components/Input/CommonInput';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import Typography from '@src/new-components/Typography';
import useForm from '@src/hooks/useForm';
import { createValidationSchema } from '@src/utils/form';
import { StyledRequireProvideEmailDialogContent } from './index.style';

const RequireProvideEmailDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);

  const validationSchema = createValidationSchema();

  const onSubmit = (values) => {
    window.open(`${PROVIDE_EMAIL_URL}/?email=${values.email}`, '_blank');
  };

  const { values, errors, handleSubmit, handleChange } = useForm({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit,
  });

  const handleCloseDialog = () => {
    onClose();
  };

  const emailRequiredEnabled = getFeatureValue(FEATURE_KEYS.EMAIL_REQUIRED, {
    userId: user?.id,
    phoneNumber: user?.phoneNumber,
  });

  const emailRequiredExpiredAt = moment(user?.requireEmailAt);

  const remainingTime = moment(emailRequiredExpiredAt)
    .startOf('day')
    .diff(moment().startOf('day'), 'days');

  const checkIsOutdated = remainingTime <= 0;

  const renderDialogContent = () => (
    <StyledRequireProvideEmailDialogContent>
      <div className="title-container">
        <Typography
          variant="body-20-bold"
          color={COLOR.darkGray}
          className="title"
        >
          {`${t('verifyEmail')} ${
            remainingTime > 0 &&
            remainingTime <= emailRequiredEnabled?.optionalWindowTime
              ? t('verifyEmailRemainingTime', { time: remainingTime })
              : ''
          }`}
        </Typography>
      </div>
      {!checkIsOutdated ? (
        <>
          <Typography
            variant="body-14-regular"
            color={COLOR.black.default}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('provideEmailDesc', {
                  date: emailRequiredExpiredAt.format('DD/MM/YYYY'),
                }),
                {
                  allowedTags: ['strong', 'span'],
                },
              ),
            }}
          />
          <Typography variant="body-14-regular" color={COLOR.black.default}>
            {t('warningIfOutdated')}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body-14-regular" color={COLOR.black.default}>
            {t('blockUsingFeatureUntilProvideEmail')}
          </Typography>
          <Typography variant="body-14-regular" color={COLOR.black.default}>
            {t('pleaseProvideEmail')}
          </Typography>
        </>
      )}
      <CommonInput
        sx={{
          '& .MuiInputBase-root': {
            padding: '6px 16px',
            borderRadius: '12px',
            boxSizing: 'border-box',
          },
        }}
        error={errors.email}
        helperText={errors.email}
        startAdornment={
          <img src={UserOutlined} alt="user-outlined" width={24} height={24} />
        }
        type="email"
        placeholder={t('inputYourEmail')}
        value={values.email}
        onChange={(e) => handleChange(e.target.value)('email')}
      />
    </StyledRequireProvideEmailDialogContent>
  );

  const renderDialogActionComponent = () => {
    if (checkIsOutdated) {
      return (
        <CommonButton
          variant="contained"
          customStyle={{
            width: '102px',
            height: '40px',
            borderRadius: '8px',
          }}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          onClick={handleSubmit}
        >
          {t('submitVerifyEmail')}
        </CommonButton>
      );
    }

    return (
      <>
        <CommonButton
          variant="outlined"
          customStyle={{
            width: '102px',
            height: '40px',
            borderRadius: '8px',
          }}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          onClick={handleCloseDialog}
        >
          {t('later')}
        </CommonButton>
        <CommonButton
          variant="contained"
          customStyle={{
            width: '102px',
            height: '40px',
            borderRadius: '8px',
          }}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          onClick={handleSubmit}
        >
          {t('submitVerifyEmail')}
        </CommonButton>
      </>
    );
  };

  return (
    <ActionDialog
      image={Crown}
      open={open}
      textContent={false}
      onClose={handleCloseDialog}
      showIconClose={!checkIsOutdated}
      contentComponent={renderDialogContent()}
      actionComponent={renderDialogActionComponent()}
      disableEscapeKeyDown={checkIsOutdated}
    />
  );
};

export default RequireProvideEmailDialog;
